import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Card,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { apiFetch } from "utils/api";

function formatDate(dateString) {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleString("de-DE");
  } catch {
    return dateString;
  }
}

const STATUS_LABELS = {
  unread: { label: "Ungelesen", color: "warning" },
  read: { label: "Gelesen", color: "success" },
};

export default function AdminNotifications() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("ALL");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fallback, setFallback] = useState(false);
  const [infoMsg, setInfoMsg] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(0);

  // Fetch customers for filter
  useEffect(() => {
    let mounted = true;
    async function fetchCustomers() {
      try {
        const data = await apiFetch("/api/admin/customers");
        const list = Array.isArray(data) ? data : data?.customers || [];
        if (mounted) setCustomers(list);
      } catch (e) {
        if (mounted) setError(e?.message || "Kunden konnten nicht geladen werden.");
      }
    }
    fetchCustomers();
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch notifications, with fallback
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfoMsg(null);
    setFallback(false);

    try {
      let data;
      const query = customerId && customerId !== "ALL" ? `?customerId=${encodeURIComponent(customerId)}` : "";

      try {
        data = await apiFetch(`/api/admin/notifications${query}`);
      } catch (err) {
        // fallback if admin endpoint not available
        if (String(err?.message || "").includes("404")) {
          setFallback(true);
          setInfoMsg("Nur Benachrichtigungen des aktuellen Kunden verfügbar.");
          data = await apiFetch("/api/notifications");
        } else {
          throw err;
        }
      }

      const notifs = Array.isArray(data) ? data : data?.notifications || [];
      setNotifications(notifs);
    } catch (e) {
      setError(e?.message || "Benachrichtigungen konnten nicht geladen werden.");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [customerId, refreshFlag]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark notification as read
  const handleMarkAsRead = async (notifId) => {
    try {
      setLoading(true);
      setError(null);

      const path = !fallback
        ? `/api/admin/notifications/${notifId}/read`
        : `/api/notifications/${notifId}/read`;

      await apiFetch(path, { method: "PATCH" });
      setRefreshFlag((f) => f + 1);
    } catch (e) {
      setError(e?.message || "Benachrichtigung konnte nicht als gelesen markiert werden.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={2} mb={2}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            <MDTypography variant="h4" fontWeight="bold">
              Benachrichtigungen
            </MDTypography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end">
            <TextField
              label="Kunde"
              select
              size="small"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              disabled={fallback}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="ALL">Alle</MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id || c.customerNumber} value={c.id || c.customerNumber}>
                  {c.companyName || c.customerNumber || c.id}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox mb={2}>
        {infoMsg && <Alert severity="info">{infoMsg}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </MDBox>
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Zeitpunkt</b></TableCell>
                <TableCell><b>Kunde</b></TableCell>
                <TableCell><b>Typ</b></TableCell>
                <TableCell><b>Nachricht</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Aktion</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : notifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Keine Benachrichtigungen gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                notifications.map((n) => {
                  const isRead = !!n.readAt;
                  return (
                    <TableRow key={n.id}>
                      <TableCell>{formatDate(n.createdAt)}</TableCell>
                      <TableCell>{n.customerNumber || n.customer?.customerNumber || "-"}</TableCell>
                      <TableCell>{n.type || "-"}</TableCell>
                      <TableCell>{n.message || n.text || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={isRead ? STATUS_LABELS.read.label : STATUS_LABELS.unread.label}
                          color={isRead ? STATUS_LABELS.read.color : STATUS_LABELS.unread.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {!isRead && (
                          <MDButton
                            size="small"
                            color="info"
                            variant="outlined"
                            onClick={() => handleMarkAsRead(n.id)}
                            disabled={loading}
                          >
                            Als gelesen
                          </MDButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Footer />
    </DashboardLayout>
  );
}
